import {
    Grid,
    FormControl,
    TextField,
    Typography,
    Box,
    Button,
    FormControlLabel,
    Checkbox
  } from "@mui/material";
  import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
  import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
  import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useState } from "react";
import { useTheme } from "@mui/material/styles";

const SilenceModal = ({ handleClose, onManageSilences, selectedSilence }: any) => {
	const theme = useTheme();
  const colors = theme.palette;
	const [silence, setSilence] = useState(selectedSilence ?? {});
  const [errorText, setErrorText] = useState<string|null>(null);
  const channels = {
    email: false,
    serviceNow: false,
    teams: false
  } as any

  if(selectedSilence?.silencedChannels?.length){
    for(let channel of selectedSilence.silencedChannels){
      channels[channel] = true
    }
  }
  const [silencedChannels, setSilencedChannels] = useState(channels);

  const handleChannelChange = (e: any) => {
    setSilencedChannels({...silencedChannels, [e.target.name]: e.target.checked})
  }

  const handleChange = ({ name, value }: any) => {
    setErrorText(null)
    setSilence((prev: any) => ({ ...prev, [name]: value }));
  };

  const checkMandatoryFields = () => {
    if(!silence.name || !silence.startTime || !silence.endTime || !silence.timeIdentifier){
        setErrorText('Please fill up all required fields.')
        return false
    }
    return true
  }

  const handleSaveSilence = () => {
    const isValid = checkMandatoryFields()
    if(!isValid) return
    const uuid = selectedSilence ? selectedSilence.uuid : crypto.randomUUID()
    const channels = Object.entries(silencedChannels).filter(([ch, isEnabled]) => !!isEnabled).map(([ch, isEnabled]) => ch)
    onManageSilences({ ...silence, isEnabled: true, uuid, silencedChannels: channels }, selectedSilence ? 'update' : 'add');
  };

  const inputSx = {
    width: '100%',

    '& .MuiOutlinedInput-root': {
      backgroundColor: `${colors.inputNew.inputBackground} !important`,
      color: `${colors.inputNew.inputText} !important`,

      '& fieldset': {
        borderColor: `${colors.inputNew.inputBorder} !important`
      },

      '&:hover fieldset': {
        borderColor: `${colors.inputNew.inputBorderHover} !important`
      },

      '&.Mui-focused fieldset': {
        borderColor: `${colors.inputNew.inputBorderFocus} !important`
      },

      '& input, & textarea': {
        color: `${colors.inputNew.inputText} !important`,
        WebkitTextFillColor: `${colors.inputNew.inputText} !important`,

        '&::placeholder': {
          color: `${colors.inputNew.inputPlaceholder} !important`,
          WebkitTextFillColor: `${colors.inputNew.inputPlaceholder} !important`,
          opacity: 1
        }
      }
    },

    '& .MuiInputBase-input': {
      color: `${colors.inputNew.inputText} !important`,
      WebkitTextFillColor: `${colors.inputNew.inputText} !important`
    },

    '& .MuiInputLabel-root': {
      color: `${colors.inputNew.inputPlaceholder} !important`
    },

    '& .MuiInputLabel-root.Mui-focused': {
      color: `${colors.inputNew.inputPlaceholder} !important`
    }
  };

  const dateTimeInputSx = {
		width: '100%',

		'& .MuiOutlinedInput-root': {
			backgroundColor: `${colors.inputNew.inputBackground} !important`,
			color: `${colors.inputNew.inputText} !important`,

			'& input': {
				padding: '15px',
				color: `${colors.inputNew.inputText} !important`,
				WebkitTextFillColor: `${colors.inputNew.inputText} !important`
			},

			'& fieldset': {
				borderColor: `${colors.inputNew.inputBorder} !important`
			},

			'&:hover fieldset': {
				borderColor: `${colors.inputNew.inputBorderHover} !important`
			},

			'&.Mui-focused fieldset': {
				borderColor: `${colors.inputNew.inputBorderFocus} !important`
			},

			'& .MuiInputAdornment-root .MuiIconButton-root': {
				color: `${colors.dateFilter.triggerText} !important`
			},

			'& .MuiInputAdornment-root .MuiSvgIcon-root': {
				color: `${colors.dateFilter.triggerText} !important`,
				fill: `${colors.dateFilter.triggerText} !important`
			},

			'&:hover .MuiInputAdornment-root .MuiIconButton-root, &.Mui-focused .MuiInputAdornment-root .MuiIconButton-root': {
				color: `${colors.dateFilter.triggerTextActive} !important`
			},

			'&:hover .MuiInputAdornment-root .MuiSvgIcon-root, &.Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': {
				color: `${colors.dateFilter.triggerTextActive} !important`,
				fill: `${colors.dateFilter.triggerTextActive} !important`
			}
		},

		'& .MuiInputLabel-root': {
			color: `${colors.inputNew.inputPlaceholder} !important`
		},

		'& .MuiInputLabel-root.Mui-focused': {
			color: `${colors.inputNew.inputPlaceholder} !important`
		},

		'& .MuiIconButton-root': {
			color: `${colors.dateFilter.triggerText} !important`
		},

		'& .MuiIconButton-root .MuiSvgIcon-root': {
			color: `${colors.dateFilter.triggerText} !important`,
			fill: `${colors.dateFilter.triggerText} !important`
		},

		'& .MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-root input': {
			padding: '9px'
		}
	};

	const dateTimePickerPopperSx = {
		'& .MuiPaper-root': {
			backgroundColor: `${colors.dateFilter.popoverBackground} !important`,
			color: `${colors.dateFilter.popoverText} !important`,
			border: `1px solid ${colors.dateFilter.popoverBorder}`,
			borderRadius: '8px',
			boxShadow: `${colors.dateFilter.popoverShadow} !important`,
			backgroundImage: 'none !important'
		},

		'& .MuiCalendarPicker-root': {
			backgroundColor: `${colors.dateFilter.calendarBackground} !important`,
			color: `${colors.dateFilter.popoverText} !important`
		},

		'& .MuiPickersCalendarHeader-root': {
			backgroundColor: `${colors.dateFilter.calendarBackground} !important`,
			color: `${colors.dateFilter.calendarHeaderText} !important`
		},

		'& .MuiPickersCalendarHeader-label': {
			color: `${colors.dateFilter.calendarHeaderText} !important`,
			fontWeight: 600
		},

		/* Month / Year dropdown icon */
		'& .MuiPickersCalendarHeader-switchViewButton': {
			color: `${colors.dateFilter.presetArrow} !important`,

			'&:hover': {
				backgroundColor: `${colors.dateFilter.triggerHoverBackground} !important`
			}
		},

		'& .MuiPickersCalendarHeader-switchViewIcon': {
			color: `${colors.dateFilter.presetArrow} !important`,
			fill: `${colors.dateFilter.presetArrow} !important`
		},

		/* Previous / Next buttons */
		'& .MuiPickersArrowSwitcher-button': {
			color: `${colors.dateFilter.presetArrow} !important`,
			border: `1px solid ${colors.dateFilter.presetArrow}`,
			borderRadius: '4px',
			width: '24px',
			height: '24px',
			padding: '3px',

			'&:hover': {
				backgroundColor: `${colors.dateFilter.triggerHoverBackground} !important`
			}
		},

		/* Reduce previous / next arrow icon size */
		'& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root': {
			fontSize: '16px !important',
			width: '16px',
			height: '16px',
			color: `${colors.dateFilter.presetArrow} !important`,
			fill: `${colors.dateFilter.presetArrow} !important`
		},

		'& .MuiDayPicker-weekDayLabel': {
			color: `${colors.dateFilter.weekDayText} !important`
		},

		'& .MuiPickersDay-root': {
			color: `${colors.dateFilter.dayText} !important`,
			backgroundColor: 'transparent',

			'&:hover': {
				backgroundColor: `${colors.dateFilter.dayHoverBackground} !important`,
				color: `${colors.dateFilter.dayHoverText} !important`
			},

			'&.Mui-selected': {
				backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
				color: `${colors.dateFilter.dayTextInRange} !important`,

				'&:hover': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				},

				'&:focus': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				}
			},

			'&.Mui-disabled': {
				color: `${colors.dateFilter.dayText} !important`,
				opacity: 0.3
			}
		},

		/* Year options */
		'& .MuiPickersYear-yearButton': {
			color: `${colors.dateFilter.dayText} !important`,

			'&:hover': {
				backgroundColor: `${colors.dateFilter.dayHoverBackground} !important`,
				color: `${colors.dateFilter.dayHoverText} !important`
			},

			'&.Mui-selected': {
				backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
				color: `${colors.dateFilter.dayTextInRange} !important`,

				'&:hover': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				},

				'&:focus': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				}
			}
		},

		'& .MuiClockPicker-root': {
			backgroundColor: `${colors.dateFilter.calendarBackground} !important`,
			color: `${colors.dateFilter.popoverText} !important`
		},

		'& .MuiClockNumber-root': {
			color: `${colors.dateFilter.dayText} !important`
		},

		'& .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb': {
			backgroundColor: `${colors.dateFilter.rangeBackground} !important`
		},

		'& .MuiClockPointer-thumb': {
			borderColor: `${colors.dateFilter.rangeBackground} !important`
		},

		'& .MuiTab-root': {
			color: `${colors.dateFilter.triggerText} !important`
		},

		'& .MuiTab-root.Mui-selected': {
			color: `${colors.dateFilter.triggerTextActive} !important`
		},

		'& .MuiTabs-indicator': {
			backgroundColor: `${colors.dateFilter.triggerText} !important`
		}
	};

	const dateTimePickerDialogSx = {
		backgroundColor: `${colors.dateFilter.popoverBackground} !important`,
		color: `${colors.dateFilter.popoverText} !important`,
		border: `1px solid ${colors.dateFilter.popoverBorder}`,
		borderRadius: '8px',
		boxShadow: `${colors.dateFilter.popoverShadow} !important`,
		backgroundImage: 'none !important',

		'& .MuiPickerStaticWrapper-root, & .MuiPickerStaticWrapper-content': {
			backgroundColor: `${colors.dateFilter.popoverBackground} !important`,
			color: `${colors.dateFilter.popoverText} !important`
		},

		'& .MuiCalendarPicker-root': {
			backgroundColor: `${colors.dateFilter.calendarBackground} !important`,
			color: `${colors.dateFilter.popoverText} !important`
		},

		'& .MuiPickersCalendarHeader-root': {
			backgroundColor: `${colors.dateFilter.calendarBackground} !important`,
			color: `${colors.dateFilter.calendarHeaderText} !important`
		},

		'& .MuiPickersCalendarHeader-label': {
			color: `${colors.dateFilter.calendarHeaderText} !important`,
			fontWeight: 600
		},

		'& .MuiPickersCalendarHeader-switchViewButton': {
			color: `${colors.dateFilter.presetArrow} !important`,

			'&:hover': {
				backgroundColor: `${colors.dateFilter.triggerHoverBackground} !important`
			}
		},

		'& .MuiPickersCalendarHeader-switchViewIcon': {
			color: `${colors.dateFilter.presetArrow} !important`,
			fill: `${colors.dateFilter.presetArrow} !important`
		},

		'& .MuiPickersArrowSwitcher-button': {
			color: `${colors.dateFilter.presetArrow} !important`,
			border: `1px solid ${colors.dateFilter.presetArrow}`,
			borderRadius: '4px',
			width: '24px',
			height: '24px',
			padding: '3px',

			'&:hover': {
				backgroundColor: `${colors.dateFilter.triggerHoverBackground} !important`
			}
		},

		'& .MuiPickersArrowSwitcher-button .MuiSvgIcon-root': {
			fontSize: '16px !important',
			width: '16px',
			height: '16px',
			color: `${colors.dateFilter.presetArrow} !important`,
			fill: `${colors.dateFilter.presetArrow} !important`
		},

		'& .MuiDayPicker-weekDayLabel': {
			color: `${colors.dateFilter.weekDayText} !important`
		},

		'& .MuiPickersDay-root': {
			color: `${colors.dateFilter.dayText} !important`,
			backgroundColor: 'transparent',

			'&:hover': {
				backgroundColor: `${colors.dateFilter.dayHoverBackground} !important`,
				color: `${colors.dateFilter.dayHoverText} !important`
			},

			'&.Mui-selected': {
				backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
				color: `${colors.dateFilter.dayTextInRange} !important`,

				'&:hover': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				},

				'&:focus': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				}
			},

			'&.Mui-disabled': {
				color: `${colors.dateFilter.dayText} !important`,
				opacity: 0.3
			}
		},

		'& .MuiPickersYear-yearButton': {
			color: `${colors.dateFilter.dayText} !important`,

			'&:hover': {
				backgroundColor: `${colors.dateFilter.dayHoverBackground} !important`,
				color: `${colors.dateFilter.dayHoverText} !important`
			},

			'&.Mui-selected': {
				backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
				color: `${colors.dateFilter.dayTextInRange} !important`,

				'&:hover': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				},

				'&:focus': {
					backgroundColor: `${colors.dateFilter.rangeBackground} !important`,
					color: `${colors.dateFilter.dayTextInRange} !important`
				}
			}
		},

		'& .MuiClockPicker-root': {
			backgroundColor: `${colors.dateFilter.calendarBackground} !important`,
			color: `${colors.dateFilter.popoverText} !important`
		},

		'& .MuiClockNumber-root': {
			color: `${colors.dateFilter.dayText} !important`
		},

		'& .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb': {
			backgroundColor: `${colors.dateFilter.rangeBackground} !important`
		},

		'& .MuiClockPointer-thumb': {
			borderColor: `${colors.dateFilter.rangeBackground} !important`
		},

		'& .MuiTab-root': {
			color: `${colors.dateFilter.triggerText} !important`
		},

		'& .MuiTab-root.Mui-selected': {
			color: `${colors.dateFilter.triggerTextActive} !important`
		},

		'& .MuiTabs-indicator': {
			backgroundColor: `${colors.dateFilter.triggerText} !important`
		},

		'& .MuiPickersToolbar-root, & .MuiDateTimePickerToolbar-root': {
			backgroundColor: `${colors.dateFilter.calendarBackground} !important`,
			color: `${colors.dateFilter.popoverText} !important`
		},

		'& .MuiPickersToolbar-root .MuiTypography-root, & .MuiDateTimePickerToolbar-root .MuiTypography-root': {
			color: `${colors.dateFilter.popoverText} !important`,
			WebkitTextFillColor: `${colors.dateFilter.popoverText} !important`,
			opacity: '1 !important'
		},

		'& .MuiPickersToolbarText-root': {
			color: `${colors.dateFilter.popoverText} !important`,
			WebkitTextFillColor: `${colors.dateFilter.popoverText} !important`,
			opacity: '1 !important'
		},

		'& .MuiDateTimePickerToolbar-dateContainer .MuiTypography-root': {
			color: `${colors.dateFilter.popoverText} !important`,
			WebkitTextFillColor: `${colors.dateFilter.popoverText} !important`,
			opacity: '1 !important'
		},

		'& .MuiDateTimePickerToolbar-timeContainer .MuiTypography-root': {
			color: `${colors.dateFilter.popoverText} !important`,
			WebkitTextFillColor: `${colors.dateFilter.popoverText} !important`,
			opacity: '1 !important'
		},

		'& .MuiPickersToolbarButton-root': {
			color: `${colors.dateFilter.popoverText} !important`
		},

		'& .MuiPickersToolbarButton-root .MuiTypography-root': {
			color: `${colors.dateFilter.popoverText} !important`,
			WebkitTextFillColor: `${colors.dateFilter.popoverText} !important`,
			opacity: '1 !important'
		},

		'& .MuiDialogActions-root': {
			backgroundColor: `${colors.dateFilter.popoverBackground} !important`
		},

		'& .MuiDialogActions-root .MuiButton-root': {
			color: `${colors.dateFilter.triggerText} !important`
		}
	};

	const checkboxSx = {
		'&&': {
			color: `${colors.checkboxGroup.checkboxBorder} !important`,
		},

		'&& .MuiSvgIcon-root': {
			color: `${colors.checkboxGroup.checkboxBorder} !important`,
			fill: `${colors.checkboxGroup.checkboxBorder} !important`,
		},

		'&&.Mui-checked .MuiSvgIcon-root': {
			color: `${colors.checkboxGroup.checkboxCheckedBackground} !important`,
			fill: `${colors.checkboxGroup.checkboxCheckedBackground} !important`,
		},

		'&&.Mui-disabled .MuiSvgIcon-root': {
			color: `${colors.checkboxGroup.disabledBorder} !important`,
			fill: `${colors.checkboxGroup.disabledBorder} !important`,
		},
	};

	const checkboxLabelSx = {
		'& .MuiFormControlLabel-label': {
			color: `${colors.checkboxGroup.optionText} !important`
		},

		'& .MuiFormControlLabel-label.Mui-disabled': {
			color: `${colors.checkboxGroup.disabledBorder} !important`
		}
	};

  return (
	<Box
		sx={{
		pr: 2,
		pb: 2,
		width: '100%',
		backgroundColor: colors.modal.bg,
		color: colors.inputNew.inputText,
		boxSizing: 'border-box'
		}}
	>
		<Grid
			container
			spacing={2}
			sx={{
				width: '100%',
				margin: 0,
			}}
			>
			{/* First Input */}
			<Grid item xs={12} sm={6}>
				<FormControl fullWidth>
					<TextField
						label="Silence Name*"
						variant="outlined"
						size="small"
						value={silence.name}
						onChange={(e) => handleChange({ name: 'name', value: e.target.value })}
						sx={inputSx}
					/>
				</FormControl>
			</Grid>

      		<Grid item xs={12} sm={6}>
				<FormControl fullWidth>
					<TextField
						label="Time Identifier*"
						variant="outlined"
						size="small"
						value={silence.timeIdentifier}
						onChange={(e) => handleChange({ name: 'timeIdentifier', value: e.target.value })}
						sx={inputSx}
					/>
				</FormControl>
			</Grid>

			{/* Date Pickers */}
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<Grid item xs={12} sm={6}>
					<FormControl fullWidth>
						<DateTimePicker
							label="Start Time (UTC)*"
							value={silence.startTime}
							onChange={(newValue) => handleChange({ name: 'startTime' , value: newValue})}
							PopperProps={{
								sx: dateTimePickerPopperSx
							}}
							DialogProps={{
								PaperProps: {
									sx: dateTimePickerDialogSx
								}
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									sx={dateTimeInputSx}
								/>
							)}
						/>
					</FormControl>
				</Grid>

				<Grid item xs={12} sm={6}>
					<FormControl fullWidth required>
						<DateTimePicker
							label="End Time (UTC)*"
							value={silence.endTime}
							onChange={(newValue) => handleChange({name: 'endTime' , value: newValue})}
							PopperProps={{
								sx: dateTimePickerPopperSx
							}}
							DialogProps={{
								PaperProps: {
									sx: dateTimePickerDialogSx
								}
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									sx={dateTimeInputSx}
								/>
							)}
						/>
					</FormControl>
				</Grid>
			</LocalizationProvider>

			{/* Second Input */}
			<Grid item xs={12}>
				<FormControl fullWidth>
					<TextField
						label="Message"
						variant="outlined"
						size="small"
						value={silence.message}
						onChange={(e) => handleChange({ name: 'message', value: e.target.value })}
						sx={inputSx}
					/>
				</FormControl>
			</Grid>

			{/* Channel Selection as Radio Buttons */}
			<Grid item xs={12}>
				<Typography
					variant="body1"
					sx={{
						color: `${colors.modal.sectionTitle} !important`,
						textAlign: 'left',
						fontWeight: 600,
						display: 'flex',
						alignItems: 'center'
					}}
				>
					Select Channels to be Silenced
					<hr
						style={{
						flexGrow: 1,
						margin: '0 5px',
						borderColor: colors.modal.sectionTitle
						}}
					/>
				</Typography>				
        <Box display="flex" gap={2} mt={1} flexWrap="wrap">
          <FormControlLabel
            control={
              <Checkbox
                checked={silencedChannels.email}
                onChange={handleChannelChange}
                name="email"
                sx={checkboxSx}
              />
            }
            label={
              <Typography
                sx={{
                  color: `${colors.checkboxGroup.optionText} !important`
                }}
              >
                Email
              </Typography>
            }
            sx={checkboxLabelSx}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={silencedChannels.serviceNow}
                onChange={handleChannelChange}
                name="serviceNow"
                sx={checkboxSx}
              />
            }
            label={
              <Typography
                sx={{
                  color: `${colors.checkboxGroup.optionText} !important`
                }}
              >
                Service Now
              </Typography>
            }
            sx={checkboxLabelSx}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={silencedChannels.teams}
                onChange={handleChannelChange}
                name="teams"
                sx={checkboxSx}
              />
            }
            label={
              <Typography
                sx={{
                  color: `${colors.checkboxGroup.optionText} !important`
                }}
              >
                Teams
              </Typography>
            }
            sx={checkboxLabelSx}
          />
        </Box>
			</Grid>

			{/* Buttons */}
			<Grid item xs={12}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 2,
						mt: 2
					}}
				>
					<Typography
						style={{
						color: colors.inputNew.error,
						cursor: 'pointer',
						marginTop: '6px',
						marginRight: '25px'
						}}					
					>
						{errorText ? `*${errorText}` : ''}
					</Typography>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
						<Button 
							variant='pepwiseButtonText'
							style={{ 
								color: colors.buttons.primary.color,
								cursor: 'pointer', 
								marginTop: '6px', 
								marginRight: '25px' 
							}} 
							onClick={handleClose} 
						> 
							CANCEL 
						</Button> 

						<Button
							variant="pepwiseSecondary"
							onClick={handleSaveSilence}
							sx={{
								backgroundColor: `${colors.buttons.primary.solidBg} !important`,
								color: `${colors.buttons.primary.solidText} !important`,

								'&:hover': {
									backgroundColor: `${colors.buttons.primary.bgColor} !important`,
									color: `${colors.buttons.primary.solidText} !important`,
								},
							}}
						> 
							SAVE 
						</Button> 
					</Box>
				</Box>
			</Grid>
		</Grid>
	</Box>
	);
};
export default SilenceModal;
